import { EmailProviderType, PersonalEmailStatus, OutlookMessageSummary, formatLocalDisplayDate } from "./personalEmailApi";

// Pure helpers extracted out of emailview.tsx so the provider -> displayed-email mapping and the POP3
// client-side search filter are unit-testable without mounting the component.

/**
 * The SMTP/POP3 branch's "connected as" address comes straight off the provider-status response (unlike Gmail,
 * whose profile lookup is a separate, deliberately-lazy call -- see fetchGmailConnectedEmail in emailview.tsx).
 * Returns "" for a non-SMTP status so callers can call this unconditionally.
 */
export function resolveSmtpConnectedEmail(status: Pick<PersonalEmailStatus, "providerType" | "fromEmail" | "username">): string {
  if (status.providerType !== EmailProviderType.CustomPopSmtp) return "";
  return status.fromEmail ?? status.username ?? "";
}

/**
 * POP3 has no server-side search (see PersonalSmtpProvider.cs) -- filters the already-fetched inbox window
 * client-side by subject/from-name instead of hiding the shared search box.
 */
export function filterThreadsBySearch<T extends { subject?: string | null; fromName?: string | null }>(
  threads: T[],
  searchTerm: string
): T[] {
  const term = searchTerm.trim().toLowerCase();
  if (!term) return threads;
  return threads.filter(
    (thread) =>
      (thread.subject ?? "").toLowerCase().includes(term) ||
      (thread.fromName ?? "").toLowerCase().includes(term)
  );
}

/**
 * Maps a page of Outlook (Graph) message summaries into the same flat shape threadlist/Inbox already renders
 * for SMTP, extended with the isStarred/isImportant flags ShowOutlookMessage's action buttons need. Extracted
 * as a pure function (mirroring the SMTP mapping precedent) so emailview.tsx's onGetOutlookMessages callback
 * stays a thin wiring layer and this reshaping logic is unit-testable without mounting the component.
 */
export function mapOutlookMessages(messages: OutlookMessageSummary[]) {
  return messages.map((m) => ({
    id: m.id,
    fromName: m.fromName ?? m.fromEmail ?? "",
    subject: m.subject,
    displayDate: m.displayDate ?? formatLocalDisplayDate(m.date),
    date: m.date,
    isStarred: m.isStarred,
    isImportant: m.isImportant,
  }));
}

/**
 * Recomputes each Gmail thread's list-row displayDate from its raw date in the viewer's local timezone, the
 * same way mapOutlookMessages/onGetInboxMessages already do for Outlook/SMTP -- the backend's own DisplayDate
 * is formatted against DateTime.UtcNow with no timezone conversion (bug/email-date-display). Keeps every other
 * field on the thread object untouched (contractor/subject/etc. come straight from the API response).
 */
export function recomputeLocalDisplayDates<T extends { date?: string | null }>(threads: T[]): T[] {
  return threads.map((thread) => ({ ...thread, displayDate: formatLocalDisplayDate(thread.date) }));
}
